/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Actions,
  Actions$inboundSchema,
  Actions$Outbound,
  Actions$outboundSchema,
} from "./actions.js";
import {
  IconTooltipContent,
  IconTooltipContent$inboundSchema,
  IconTooltipContent$Outbound,
  IconTooltipContent$outboundSchema,
} from "./icontooltipcontent.js";

/**
 * The variant of the icon
 */
export const IconConfigVariant = {
  Caution: "caution",
  Critical: "critical",
  Default: "default",
  Eai: "eai",
  EStop: "eStop",
  Faulted: "faulted",
  ModeCaution: "mode_caution",
  ModeConserving: "mode_conserving",
  ModeCooling: "mode_cooling",
  ModeDefault: "mode_default",
  ModeSecondary: "mode_secondary",
  ModeTertiary: "mode_tertiary",
  ModeWarning: "mode_warning",
  Off: "off",
  On: "on",
  Oos: "oos",
  Satisfied: "satisfied",
  Warning: "warning",
} as const;
/**
 * The variant of the icon
 */
export type IconConfigVariant = OpenEnum<typeof IconConfigVariant>;

/**
 * Specifies configuration for an icon.
 */
export type IconConfig = {
  /**
   * The actions that are executed when the icon is clicked.
   */
  actions?: Array<Actions> | undefined;
  /**
   * The name of the icon
   */
  icon: string;
  /**
   * Whether the icon is pulsing
   */
  isPulsing?: boolean | undefined;
  /**
   * The label of the icon in this state
   */
  label?: string | undefined;
  /**
   * The tooltip content of the icon, displays the given value, or the current value of the ref, or both if both are provided.
   */
  tooltipContent?: IconTooltipContent | undefined;
  /**
   * The variant of the icon
   */
  variant?: IconConfigVariant | undefined;
};

/** @internal */
export const IconConfigVariant$inboundSchema: z.ZodType<
  IconConfigVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(IconConfigVariant);
/** @internal */
export const IconConfigVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  IconConfigVariant
> = openEnums.outboundSchema(IconConfigVariant);

/** @internal */
export const IconConfig$inboundSchema: z.ZodType<
  IconConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  actions: types.optional(z.array(Actions$inboundSchema)),
  icon: types.string(),
  is_pulsing: types.boolean().default(false),
  label: types.optional(types.string()),
  tooltip_content: types.optional(IconTooltipContent$inboundSchema),
  variant: IconConfigVariant$inboundSchema.default("default"),
}).transform((v) => {
  return remap$(v, {
    "is_pulsing": "isPulsing",
    "tooltip_content": "tooltipContent",
  });
});
/** @internal */
export type IconConfig$Outbound = {
  actions?: Array<Actions$Outbound> | undefined;
  icon: string;
  is_pulsing: boolean;
  label?: string | undefined;
  tooltip_content?: IconTooltipContent$Outbound | undefined;
  variant: string;
};

/** @internal */
export const IconConfig$outboundSchema: z.ZodType<
  IconConfig$Outbound,
  z.ZodTypeDef,
  IconConfig
> = z.object({
  actions: z.array(Actions$outboundSchema).optional(),
  icon: z.string(),
  isPulsing: z.boolean().default(false),
  label: z.string().optional(),
  tooltipContent: IconTooltipContent$outboundSchema.optional(),
  variant: IconConfigVariant$outboundSchema.default("default"),
}).transform((v) => {
  return remap$(v, {
    isPulsing: "is_pulsing",
    tooltipContent: "tooltip_content",
  });
});

export function iconConfigToJSON(iconConfig: IconConfig): string {
  return JSON.stringify(IconConfig$outboundSchema.parse(iconConfig));
}
export function iconConfigFromJSON(
  jsonString: string,
): SafeParseResult<IconConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IconConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IconConfig' from JSON`,
  );
}
